import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Sidebar } from "../components/Sidebar";
import { Header } from "../components/Header";
import HighPerformanceAd from "../components/HighPerformanceAd";
import { Footer } from "../components/ Footer";
import axios from '../axiosInstance';

// Initialisation ChartJS (tu peux garder si tu affiches des graphiques)
import { Chart as ChartJS, LineElement, PointElement, LinearScale, CategoryScale } from "chart.js";
ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale);

function Toast({ message, type = "error", onClose }) {
  return (
    <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded shadow-lg text-white ${type === "error" ? "bg-red-600" : "bg-green-600"}`}>
      <div className="flex items-center space-x-2">
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white hover:text-gray-300 font-bold">×</button>
      </div>
    </div>
  );
}

export default function Parameters({ onLogout }) {
  const [options, setOptions] = useState(0);
  const [coinz, setCoinz] = useState(0);
  const [selfWeeklyProfit, setSelfWeeklyProfit] = useState(0);
  const [weeklyProfit, setWeeklyProfit] = useState(0);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [url, setUrl] = useState("");
  const [userAds, setUserAds] = useState([]);
  const [views, setViews] = useState(2000); // Valeur par défaut

  const showToast = (message, type = "error") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  // Récupérer les annonces de l'utilisateur
  const fetchUserAds = async () => {
    try {
      const response = await axios.get("/api/autosurf/my-ads", { withCredentials: true });
      setUserAds(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des annonces :", error);
    }
  };

  // Récupérer les données utilisateur (coinz, options, etc)
  const getUserData = () => {
    axios.get(`/api/user/me`, { withCredentials: true })
      .then((response) => {
        const { coinz, options, systemCoinz, weeklyProfit } = response.data;
        const valeurCalculee = options > 0 ? ((systemCoinz / weeklyProfit) * options) : 0;
        setSelfWeeklyProfit(valeurCalculee);
        setCoinz(coinz);
        setOptions(options);
        setWeeklyProfit(weeklyProfit);
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des données:", error);
      });
  };

  // Soumettre une nouvelle annonce
  const handleAdSubmit = async () => {
    if (!url || url.trim() === "") {
      showToast("Please enter a valid URL.", "error");
      return;
    }

    try {
      const response = await axios.post(
        "/api/autosurf/submit-ad",
        { url, views },
        { withCredentials: true }
      );

      if (response.status === 200) {
        showToast(`Ad submitted successfully! ${views} XP awarded.`, "success");
        setUrl("");
        getUserData();
        fetchUserAds();
      }
    } catch (error) {
      if (error.response && error.response.data?.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An unexpected error occurred.", "error");
      }
    }
  };

  // Renouveler une annonce existante
  const handleRenewAd = async (adId) => {
    try {
      const response = await axios.post(
        "/api/autosurf/renew-ad",
        { adId },
        { withCredentials: true }
      );

      if (response.status === 200) {
        showToast("Ad renewed successfully!", "success");
        getUserData();
        fetchUserAds();
      }
    } catch (error) {
      if (error.response?.data?.message) {
        showToast(error.response.data.message, "error");
      } else {
        showToast("An unexpected error occurred.", "error");
      }
    }
  };

  //0.7.6 - Nouveau boutons
  const handlePauseAd = async (adId) => {
    try {
      await axios.post('/api/autosurf/pause-ad', { adId }, { withCredentials: true });
      showToast("Ad paused.", "success");
      fetchUserAds();
    } catch (err) {
      showToast("Failed to pause ad.", "error");
    }
  };
  
  const handleReactivateAd = async (adId) => {
    try {
      await axios.post('/api/autosurf/reactivate-ad', { adId }, { withCredentials: true });
      showToast("Ad reactivated.", "success");
      fetchUserAds();
    } catch (err) {
      showToast("Failed to reactivate ad.", "error");
    }
  };
  
  const handleDeleteAd = async (adId) => {
    try {
      await axios.post('/api/autosurf/delete-ad', { adId }, { withCredentials: true });
      showToast("Ad deleted.", "success");
      fetchUserAds();
    } catch (err) {
      showToast("Failed to delete ad.", "error");
    }
  };


  useEffect(() => {
    document.title = "Buy ads | CoinzUp";
    getUserData();
    fetchUserAds();
  }, []);

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "" })}
        />
      )}

      <div className="min-h-screen flex flex-col bg-gray-900 text-white font-sans">
        <div className="flex flex-1 min-h-[calc(100vh-64px)]">
          <Sidebar onLogout={onLogout} />

          <main className="flex-1 px-4 sm:px-10 py-8 space-y-10 overflow-auto min-w-0">
            <Header
              title="Buy ads"
              balance={coinz}
              optionsOwned={options}
              estimatedWeeklyGain={selfWeeklyProfit}
            />

            <HighPerformanceAd keyId="fd5da3543c594f07b73630f461f10613" width={728} height={90} />

            <div className="bg-gray-800/70 p-6 rounded-lg shadow border border-gray-700 mb-6">
              <h2 className="text-xl font-semibold mb-4">How Ads Work</h2>
              <p className="text-gray-300 text-sm leading-relaxed">
                When you submit a URL, you must purchase a <strong className="text-blue-400">minimum of 2000 views</strong>. You can choose to buy more, in multiples of 100. Each view costs 1 Coinz and gives you 1 XP.
              </p>
              <p className="text-gray-300 text-sm mt-3 leading-relaxed">
                Your ad will be shown in our <strong className="text-blue-400">autosurf and PTP networks</strong>. Each time another user visits your site, one view is deducted. Once all views are used, your ad automatically expires.
              </p>
              <p className="text-gray-300 text-sm mt-3 leading-relaxed">
                You can pause or reactivate your ad at any time. However, if you delete your ad manually, <strong className="text-red-400">no Coinz will be refunded</strong>, even if views remain unused.
              </p>
              <p className="text-gray-300 text-sm mt-3 leading-relaxed">
                Future updates will allow you to extend ads, purchase additional views, or set advanced targeting options.
              </p>

            </div>

            <div className="bg-gray-800/70 p-6 rounded-lg shadow border border-gray-700 mb-6">
              <h2 className="text-xl font-semibold mb-4">Buy Targeted Visits for the autosurf & PTP</h2>

              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm text-gray-300">Website URL</label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="w-full px-4 py-2 rounded bg-gray-900 border border-gray-600 text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm text-gray-300">Number of views</label>
                  <input
                    type="number"
                    min="2000"
                    step="100"
                    value={views}
                    onChange={(e) => setViews(parseInt(e.target.value))}
                    className="w-full px-4 py-2 rounded bg-gray-900 border border-gray-600 text-white"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    Minimum: <span className="text-blue-400 font-semibold">2000 views</span>. <br />
                    Cost: <span className="text-blue-400 font-semibold">{views} Coinz</span> — You’ll earn <span className="text-green-400 font-semibold">{views} XP</span>
                  </p>
                </div>

                <Button
                  onClick={handleAdSubmit}
                  type="submit"
                  disabled={!url || views < 2000 || coinz < views}
                  className="mt-4 w-full"
                >
                  Submit Ad ({views} Coinz → {views} XP)
                </Button>
      
                {views < 2000 && (
                  <p className="text-red-500 text-sm mt-2">
                    You must purchase at least 2000 views to submit an ad.
                  </p>
                )}

                {coinz < views && views >= 2000 && (
                  <p className="text-red-500 text-sm mt-2">
                    You don't have enough Coinz for {views} views.
                  </p>
                )}
              </div>
            </div>


            <div className="bg-gray-800/70 p-6 rounded-lg shadow border border-gray-700">
              <h2 className="text-xl font-semibold mb-4">Your Active Ads</h2>

              <table className="w-full table-auto text-left text-sm">
                <thead className="bg-gray-700 text-gray-300">
                  <tr>
                    <th className="py-2 px-3">URL</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Views left</th>
                    <th className="py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {userAds.length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-4 text-center text-gray-400">No ads submitted yet.</td>
                    </tr>
                  )}

                  {userAds.map((ad) => (
                    <tr key={ad.id} className="border-t border-gray-700">
                      <td className="py-2 px-3 break-all text-blue-400">{ad.url}</td>
                      <td className="py-2 px-3 capitalize text-gray-300">{ad.status}</td>
                      <td className="py-2 px-3 text-gray-200">{ad.views_left ?? 0}</td>
                      <td className="py-2 px-3 space-x-2">
                        {ad.status === "expired" && (
                          <button
                            onClick={() => handleRenewAd(ad.id)}
                            className="px-3 py-1 text-sm bg-blue-500 hover:bg-blue-400 text-white rounded shadow-sm"
                            disabled={coinz < views}
                          >
                            Renew
                          </button>
                        )}
                        {ad.status === "paused" ? (
                          <button
                            onClick={() => handleReactivateAd(ad.id)}
                            className="px-3 py-1 text-sm bg-green-600 hover:bg-green-500 text-white rounded shadow-sm"
                          >
                            Reactivate
                          </button>
                        ) : (
                          <button
                            onClick={() => handlePauseAd(ad.id)}
                            className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 text-white rounded shadow-sm"
                          >
                            Pause
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAd(ad.id)}
                          className="px-3 py-1 text-sm text-red-400 hover:text-red-300 underline rounded"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>



            <HighPerformanceAd keyId="96e99453a24be0353f2fe26dc0b9b728" width={468} height={60} />
          </main>
        </div>

        <Footer />
      </div>
    </>
  );
}
